import {div, el, fitMinWidthToText, getCSSRule, getStyleSheet, onAdded, setText} from '@megaphonenpm/mputils/src/DOM.js';
import {isNil, notNil} from '@megaphonenpm/mputils/src/utils.js';
import {cloneInstance} from "../GWDService.js";
import {cancelSequentialPlay, playSequentially} from "../Animation.js";
import {MegaScreen} from "../MegaScreen.js";
import {
  STATE_MAIN_SCOREBOARD,
  STATE_SCENE,
  STATE_SCENE_RESULT,
  STATE_SCENE_SCOREBOARD
} from '@megaphonenpm/mrtsclient/src/constants.js';
import {
  CHANGE
} from '@megaphonenpm/mputils/src/constants.js';
import {ScreenComponent} from "./ScreenComponent.js";
import {get} from 'lodash-es';

export class ScoreboardComponent extends ScreenComponent {
  constructor (themeID) {
    super(themeID, [], {
      options: null,
      className: 'scoreboard',
      rank_txt_id: null,
      name_txt_id: null,
      points_txt_id: null,
      pendingIntroAnimationID: null
    });
  }

  validate () {
    super.validate();

    if (isNil(this.options)) {
      this.valid = false;
    }
  }

  configure ({id,
              visibleStates,
              visibleProducts,
              numRows,
              numCols,
              rowGap,
              columnGap,
              rankGap,
              hideEmptyRows}) {
    this.options = arguments[0];
    this.rank_txt_id = `${id}_rank_txt`;
    this.name_txt_id = `${id}_name_txt`;
    this.points_txt_id = `${id}_points_txt`;
    this.pointsSuffix = this.getThemeSetting(`design.scoreboards.screen.${this.options.id}.points.suffix.visible`) ?
      this.getLocaleString('points_suffix') : '';
    this.nameHAlign = this.getThemeSetting(`design.scoreboards.screen.${this.options.id}.name.align.horizontal`, 'left');

    this.idsOfRequiredElements = [this.name_txt_id];
    this.validate();
    if (this.valid) {
      this.registerForAppStateChanges();
    }
  }

  /**
   * Creates and populates a scoreboard as a grid of rows and columns.
   *
   * TODO: Add optional ordinals (1st, 2nd, 3rd), as specified by theme setting.
   */
  build (rankings) {
    super.build();

    // Pre-flight check
    if (!this.valid) {
      return;
    }

    // Determine number of items required to fill the specified rows and columns.
    // If number of rows and columns are not provided, default to 10 and 1, respectively.
    let numRows = notNil(this.options.numRows) ? this.options.numRows : 10;
    let numCols = notNil(this.options.numCols) ? this.options.numCols : 1;
    let requiredNumItems =numRows * numCols;

    // TODO: This code modifies the CSS rule for all scoreboards. In future, if MegaScreen ever
    //   needed to show multiple scoreboards simultaneously, each with its own number of rows,
    //   then this approach would need to be modified to apply the styles to the specific scoreboard
    //   being created here only, not to the general .scoreboard CSS class.
    let scoreboardRule = getCSSRule(getStyleSheet("main"), ".applicationRoot .top.active .scoreboard");
    scoreboardRule.style['grid-template-rows'] = `repeat(${this.options.numRows}, min-content)`;
    let rowGap = notNil(this.options.rowGap) ? `${this.options.rowGap}px` : '';
    let columnGap = notNil(this.options.columnGap) ? `${this.options.columnGap}px` : '';
    scoreboardRule.style['row-gap'] = rowGap;
    scoreboardRule.style['column-gap'] = columnGap;

    // Create the top-level scoreboard container
    this.view = el('ul', this.options.id, this.className);

    // Create the HTML boilerplate for a single scoreboard item...
    // Outer <li>
    let listItem = el('li');
    // .scoreboard-item container
    let scoreboardItem = div(null, 'scoreboard-item');
    listItem.append(scoreboardItem);
    // Rank
    let rank = cloneInstance(this.themeID, this.rank_txt_id);
    if (notNil(rank)) {
      rank.classList.add('rank');
      rank.style.marginRight = notNil(this.options.rankGap) ? `${this.options.rankGap}px` : '';
    }
    // User name
    let username = cloneInstance(this.themeID, this.name_txt_id);
    username.classList.add('username');
    username.style.textAlign = this.nameHAlign;

    // Points
    let points = cloneInstance(this.themeID, this.points_txt_id);
    if (notNil(points)) {
      points.classList.add('points');
    }

    // Assign minimum widths for the "Rank" and "Points" columns in the scoreboard. Note that this
    // is effective after the element's .font style is available only, so delay the assignment until
    // onAdded() has resolved.
    if (notNil(rank)) {
      onAdded(rank, scoreboardItem).then(() => {
        // TODO: Handle ordinals when the are implemented
        fitMinWidthToText(this.view, '.rank', '99');
      });
      scoreboardItem.append(rank);
    }
    scoreboardItem.append(username);
    if (notNil(points)) {
      onAdded(points, scoreboardItem).then(() => {
        fitMinWidthToText(this.view, '.points', `9999${this.pointsSuffix}`);
      });
      scoreboardItem.append(points);
    }

    // Create all scoreboard items based on the preceding HTML boilerplate
    for (var i = 0; i < requiredNumItems; i++) {
      this.view.append(listItem.cloneNode(true));
    }

    // Hide empty rows
    if (this.options.hideEmptyRows) {
      this.hideEmptyRows();
    }

    // Add CSS classes to control per-state scoreboard visibility
    this.applyVisibleStateClasses();

    // Add CSS classes indicating the products during which the scoreboard should be visible
    this.applyVisibleProductClasses();

    // Add CSS classes indicating the products during which the scoreboard should be hidden
    this.applyHiddenProductClasses();

    // Set on-screen text content based on the current state's rankings data
    this.update();
  }

  /**
   * Sets on-screen text content based on the current state's rankings data
   */
  update () {
    let rankings = this.getCurrentStateRankings();
    let scoreboardItems = Array.from(this.view.querySelectorAll('.scoreboard-item'));
    scoreboardItems.forEach((item, index) => {
      setText([item.querySelector('.rank')],index+1);
      let name = '';
      let points = '';
      if (notNil(rankings) && notNil(rankings[index])) {
        name = rankings[index].label;
        points = rankings[index].points === '' ? '' : rankings[index].score + this.pointsSuffix;
      }
      setText([item.querySelector('.username')], name);
      setText([item.querySelector('.points')], points);
    });
  }

  /**
   * Plays the overall scoreboard intro animation.
   */
  playIntro () {
    this.playItemIntros();
  }

  /**
   * Plays each scoreboard row's start animation, delayed by the amount specified by the theme.
   *
   * @param from The index of the FIRST item (row) to play. If not provided, defaults to 0.
   * @param to The index AFTER the LAST item (row) to play. If not provided, defaults to either
   *   the number of rankings in appState.rankings (when hideEmptyRows is true) or all .scoreboard-items
   *   in the scoreboard (when hideEmptyRows is false).
   */
  playItemIntros(from, to) {
    let scoreboardItemStartAnimation = this.getThemeSetting(`design.scoreboards.screen.${this.options.id}.item.animation.start.label`);
    if (notNil(scoreboardItemStartAnimation)) {
      let scoreboardAnimationDelay = this.getThemeSetting(`design.scoreboards.screen.${this.options.id}.main.animation.start.delay`);
      let scoreboardItemAnimationDelay = this.getThemeSetting(`design.scoreboards.screen.${this.options.id}.item.animation.start.delay`);
      let scoreboardItems = Array.from(this.view.querySelectorAll('.scoreboard-item'));

      // Hide, and don't animate, scoreboard rows with no ranking data
      if (this.options.hideEmptyRows) {
        // Hide row
        this.hideEmptyRows();
        // Skip animation
        scoreboardItems = scoreboardItems.slice(0, MegaScreen.globals.appState.sceneRankings.length);
      }
      // Don't animate any scoreboard rows at or after the supplied "to" index
      if (notNil(to)) {
        scoreboardItems = scoreboardItems.slice(0, to);
      }
      // Don't animate any scoreboard rows before the supplied "from" index
      if (notNil(from)) {
        scoreboardItems = scoreboardItems.slice(from);
      }

      // Before starting intro animations, hide all rows that will be animated
      scoreboardItems.forEach(item => {
        item.style.visibility = 'hidden';
      });

      // Play the intro animation for all specified rows
      this.pendingIntroAnimationID = playSequentially(scoreboardItems,
        scoreboardItemStartAnimation,
        scoreboardAnimationDelay,
        scoreboardItemAnimationDelay,
        animationID => {
          this.pendingIntroAnimationID = null;
          scoreboardItems.forEach(scoreboardItem => {
            scoreboardItem.style.removeProperty('visibility');
          });
        });
    }
  }

  getCurrentStateRankings () {
    if (MegaScreen.globals.appState.state === STATE_SCENE
      || MegaScreen.globals.appState.state === STATE_SCENE_RESULT) {
      return MegaScreen.globals.appState.sceneRankings;
    } else if (MegaScreen.globals.appState.state === STATE_SCENE_SCOREBOARD) {
      return MegaScreen.globals.appState.sceneScoreboardRankings;
    } else if (MegaScreen.globals.appState.state === STATE_MAIN_SCOREBOARD) {
      return MegaScreen.globals.appState.overallRankings;
    }
  }

  hideEmptyRows () {
    let numItems = get(this.getCurrentStateRankings(), 'length');
    if (notNil(numItems)) {
      Array.from(this.view.querySelectorAll('.scoreboard-item'))
        .slice(numItems).forEach(item => {
          item.style.visibility = 'hidden';
        });
    }
  }

  applyVisibleStateClasses () {
    // Add CSS classes for all states during which the view should be visible
    this.options.visibleStates.forEach(state => {
      this.view.classList.add(state);
    });
  }

  applyVisibleProductClasses () {
    // Add CSS classes for all products during which the view should be visible
    if (notNil(this.options.visibleProducts)) {
      this.options.visibleProducts.forEach(productName => {
        this.view.classList.add(`product_${productName}`);
      });
    }
  }

  applyHiddenProductClasses () {
    // Add CSS classes for all products during which the view should be visible
    if (notNil(this.options.hiddenProducts)) {
      this.options.hiddenProducts.forEach(productName => {
        this.view.classList.add(`hide_product_${productName}`);
      });
    }
  }

  detach () {
    if (document.body.contains(this.view)) {
      this.view.remove();
    }
  }

  registerForAppStateChanges () {
    MegaScreen.globals.appState.on(CHANGE, e => {
      this.syncToState(e.detail);
    });
    this.syncToState();
  }

  /**
   * Updates the scoreboard when the app state changes
   */
  syncToState (changedProp) {
    if (this.active && this.options.visibleStates.includes(MegaScreen.globals.appState.state)) {
      // Handle changes to rankings mid-scene (as users answer questions)
      if (get(changedProp, 'prop') === 'sceneRankings') {
        let oldRankings = changedProp.oldValue;
        let newRankings = changedProp.value;
        // For each user in the new scene rankings, assign text content for rank, user name, and points
        this.update(newRankings);
        // Play the intros for any new users in the rankings. Note that a more sophisticated implementation
        // would play intros for any items that are new, no matter what the position, but this implementation
        // is built to service the single use case of items that are added to the end of the rankings list only.
        // Playing animations for changes to the middle of the list is not a currently supported use case.
        if (this.options.hideEmptyRows) {
          // Don't play the item intros unless this scoreboard is in "Hide Empty Rows" mode
          // (otherwise the item intro would play twice when new data becomes available, which
          // looks like a visual glitch).
          this.playItemIntros(oldRankings.length, newRankings.length);
        }
      }
    } else if (get(changedProp, 'prop') === 'state') {
      // If a scoreboard row animation is in progress when the main application state changes,
      // cancel the pending animation.
      if (notNil(this.pendingIntroAnimationID)) {
        cancelSequentialPlay(this.pendingIntroAnimationID);
      }
    }
  }

  /**
   * Returns true if this scoreboard is currently on screen.
   */
  get active () {
    return this.themeID === MegaScreen.globals.themeService.getCurrentScreenThemeID();
  }
}
